import React from 'react';
import { FormGroup, Input, InputNumber, TextArea, Tooltip } from 'ps-design';
import Select from 'ps-components/ReactSelect/ReactSelect';
import { IOption } from './types/privateSurvey';

const DEFAULT_CURRENCY_MESSAGE = "This value cannot be modified.  It is defaulted to the selected job or site currency.";
const SURVEY_CODE_MESSAGE = "The minimum length for this field is 3 characters and the last two characters must be the last two digits of the survey year (ex. MP-14 if the year of the survey is 2014)";

export interface IInputParams {
  className?: string;
  error?: string | boolean | null;
  label: string;
  id: string;
  isDisabled?: boolean | false;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (arg0: any, arg1?: any) => void;
  options?: IOption[];
  required?: boolean;
  rows?: number;
  value: string | undefined;
}

export const formatInputValue = (event: React.FocusEvent<HTMLInputElement>, currValue: string | undefined, onChange: (id: string, value: string) => void) => {
  const { id, value } = event.target;
  let updatedValue = value;
  if (id.includes('pct') && updatedValue.length > 1 && !updatedValue.includes('.')) {
    updatedValue = formatPctValue(value);
  }

  if (updatedValue !== currValue) onChange(id, updatedValue)
}

export const formatPctValue = (value: string) => {
  let pct = value.split('');
  pct.splice(pct.length - 2, 0, ".");
  return pct.join('');
}

export const getFormGroupLabelForRequiredField = (labelText: string, disabled?: boolean) => {
  const isCurrencyInput = labelText === "Currency";
  const isSurveyCodeInput = labelText === "Survey Code";
  const showTooltip = (disabled && isCurrencyInput) || isSurveyCodeInput;
  const tooltipText = isCurrencyInput ? DEFAULT_CURRENCY_MESSAGE : SURVEY_CODE_MESSAGE;

  return (
    <label className="form-control__label pxl-control-label">
      {showTooltip ? (
        <Tooltip
          hoverText={tooltipText}
          id='tooltipDefault'
          tooltipText={labelText}
        />
      ) : labelText}
      <span className="required">*</span>
    </label>
  );
}

export const renderInput = (params: IInputParams) => {
  return (
    <div className="col-md-6">
      <FormGroup error={params.error} labelClassName="form-control__label" text={params.required ? null : params.label}>
        {params.required && getFormGroupLabelForRequiredField(params.label)}
        <Input
          id={params.id}
          disabled={params.isDisabled}
          onChange={params.onChange}
          value={params.value}
        />
      </FormGroup>
    </div>
  );
}

export const renderNumberInput = (params: IInputParams) => {
  return (
    <div className="col-md-6">
     <FormGroup error={params.error} labelClassName="form-control__label" text={params.required ? null : params.label}>
       {params.required && getFormGroupLabelForRequiredField(params.label)}
        <InputNumber
          id={params.id}
          disabled={params.isDisabled}
          min={0}
          onBlur={(e: React.FocusEvent<HTMLInputElement>, inputValue: string ) => formatInputValue(e, params.value, params.onChange)}
          onChange={params.onChange}
          value={params.value}
        />
     </FormGroup>
    </div>
  );
}

export const renderSelect = (params: IInputParams) => {
 const value = params.options?.find(option => option.text === params.value) || null;
  return (
    <FormGroup className={params?.className || ''} text={params.required ? null : params.label}>
     {params.required && getFormGroupLabelForRequiredField(params.label, params.isDisabled)}
      <Select
        getOptionLabel={(option: IOption) => option.value}
        getOptionValue={(option: IOption) => option.text}
        isDisabled={params.isDisabled}
        id={params.id}
        instanceId={`${params.label.toLowerCase()}-select`}
        onChange={(option: IOption) => params.onChange({id: params.id, value: option.text})}
        options={params.options}
        value={value}
      />
    </FormGroup>
  );
}

export const renderTextArea = (params: IInputParams) => {
  return (
    <div className="col-md-12">
      <FormGroup error={params.error} labelClassName="form-control__label" text={params.required ? null : params.label}>
        <TextArea
          id={params.id}
          onChange={params.onChange}
          rows={params.rows}
          value={params.value}
        />
      </FormGroup>
    </div>
  );
}

export const validateInput = (value: string) => {
  const htmlTagCheck = /<\/?[a-z][\s\S]*>/i;
  return htmlTagCheck.test(value);
}
